import "@/index.css";
import { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PortalLayout, AdminLayout, SupLayout } from "@/components/Layout";
import PledgeModal from "@/components/PledgeModal";
import Landing from "@/pages/Landing";
import { LoginPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";
import Dashboard from "@/pages/portal/Dashboard";
import Students from "@/pages/portal/Students";
import Staff from "@/pages/portal/Staff";
import Opportunities from "@/pages/portal/Opportunities";
import Gallery from "@/pages/portal/Gallery";
import Certificates from "@/pages/portal/Certificates";
import Challenges from "@/pages/portal/Challenges";
import Proposals from "@/pages/portal/Proposals";
import Resources from "@/pages/portal/Resources";
import Reports from "@/pages/portal/Reports";
import Settings from "@/pages/portal/Settings";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSchools from "@/pages/admin/AdminSchools";
import AdminOpportunities from "@/pages/admin/AdminOpportunities";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCompare from "@/pages/admin/AdminCompare";
import AdminCoordinators from "@/pages/admin/AdminCoordinators";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminChallenges from "@/pages/admin/AdminChallenges";
import AdminResources from "@/pages/admin/AdminResources";
import AdminMarquee from "@/pages/admin/AdminMarquee";
import AdminCirculars from "@/pages/admin/AdminCirculars";
import AdminPledges from "@/pages/admin/AdminPledges";
import AdminFortyHours from "@/pages/admin/AdminFortyHours";
import PortalFortyHours from "@/pages/portal/PortalFortyHours";
import CircularsIn from "@/pages/portal/CircularsIn";
import SupDashboard from "@/pages/sup/SupDashboard";
import AdminSupervisors from "@/pages/admin/AdminSupervisors";

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="font-head font-bold text-turq-600 animate-pulse">جارٍ التحميل …</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : user.role === "supervisor" ? "/sup" : "/portal"} replace />;
  return children;
}

function PledgeGate({ children }) {
  const { user } = useAuth();
  const [ok, setOk] = useState(false);
  if (user && user.role === "coordinator" && !user.imp_by && !user.pledge_accepted && !ok) {
    return <PledgeModal onAccept={() => setOk(true)} />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors dir="rtl" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/portal" element={<Guard role="coordinator"><PledgeGate><PortalLayout /></PledgeGate></Guard>}>
            <Route index element={<Dashboard />} />
            <Route path="students" element={<Students />} />
            <Route path="staff" element={<Staff />} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="certificates" element={<Certificates />} />
            <Route path="challenges" element={<Challenges />} />
            <Route path="proposals" element={<Proposals />} />
            <Route path="resources" element={<Resources />} />
            <Route path="circulars" element={<CircularsIn />} />
            <Route path="reports" element={<Reports />} />
            <Route path="forty-hours" element={<PortalFortyHours />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="/sup" element={<Guard role="supervisor"><SupLayout /></Guard>}>
            <Route index element={<SupDashboard />} />
          </Route>
          <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
            <Route index element={<AdminDashboard />} />
            <Route path="schools" element={<AdminSchools />} />
            <Route path="supervisors" element={<AdminSupervisors />} />
            <Route path="opportunities" element={<AdminOpportunities />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="compare" element={<AdminCompare />} />
            <Route path="coordinators" element={<AdminCoordinators />} />
            <Route path="certificates" element={<AdminCertificates />} />
            <Route path="challenges" element={<AdminChallenges />} />
            <Route path="resources" element={<AdminResources />} />
            <Route path="marquee" element={<AdminMarquee />} />
            <Route path="circulars" element={<AdminCirculars />} />
            <Route path="pledges" element={<AdminPledges />} />
            <Route path="forty-hours" element={<AdminFortyHours />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
